import { createLokiLogger, LokiEnvironment, LokiServiceType } from '@pleased-npm/loki-logger';
import * as env from '@/common/environment';
import { store } from '@/store';
import type * as ApiModel from './models.pleased';
import * as SdkConfig from '@/pleased-sdk/PleasedClient/client.config';
import type { FirebaseConnectionTelemetry } from '@/pleased-sdk/PleasedChat/FirebaseConnectionMonitor';
import { buildInitEventTelemetry } from '@/common/logs/initEventTelemetry';
import type { InitializeOptions } from '@/common/types';
import { FirebaseConnectionOutcome } from '@/pleased-sdk/PleasedChat/FirebaseConnection.enum';

declare const GLOBAL_BUILD_PROFILE: `${'sportybet' | 'football' | 'universal'}-${'prod' | 'stage'}`;
declare const GLOBAL_APP_VERSION: string;

const EVENT_WEB_URL = 'https://event.pls-696e7465726e616c.link/event.web';

const isProdEnv = GLOBAL_BUILD_PROFILE.includes('prod');

const getInitialTenant = (): string => {
  try {
    const url = env.get('url');
    return url ? getTenantFromUrl(url) : 'unknown';
  } catch {
    return 'unknown';
  }
};

/**
 * Post to the legacy event.web endpoint without API client headers
 * (e.g. language) that would trigger a CORS preflight failure.
 */
const postLegacyEventLog = async (log: unknown): Promise<void> => {
  await fetch(EVENT_WEB_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(log),
    keepalive: true,
  });
};

export const logger = createLokiLogger({
  lokiUrl: isProdEnv
    ? 'https://event.pls-696e7465726e616c.link/loki/api/v1/push'
    : 'https://event-uat.pleased2.com/loki/api/v1/push',
  labels: {
    env: isProdEnv ? LokiEnvironment.PROD : LokiEnvironment.STAGE,
    service: 'chatbot-web',
    service_type: LokiServiceType.FE,
    tenant: getInitialTenant(),
    version: GLOBAL_APP_VERSION || 'unknown',
  },
  batchTimeout: 10000,
  debug: !isProdEnv,
});

/**
 * Log error events to the Pleased event tracking service
 */
export async function logErrorEvents(error: ApiModel.LogErrorEvent) {
  try {
    const userStore = (
      store.state as {
        user: { pleasedUser: { userId?: string } };
      }
    ).user;
    const userId = userStore.pleasedUser.userId || '';

    const brandCode = env.get('brandCode');
    const url = env.get('url');
    const environment = GLOBAL_BUILD_PROFILE;
    const eventTime = new Date().toISOString();
    const additionalData = error ? JSON.stringify(error) : '';

    // Push logs to Loki
    logger.error(`[${eventTime}] [FE-CHATBOT-LOGS] Brand code: ${brandCode}, url: ${url}, userId: ${userId}`, {
      event: error.event,
      exception_name: error.event,
      exception_message: error.message,
      exception_url: error.url,
      exception_code: error.code,
      exception_data: error.data,
      ...(await getDefaultMetadata()),
    });

    // Still collection logs in old format just in case
    const log = {
      level: 'info',
      log: `[${eventTime}] [FE-CHATBOT-LOGS] Brand code: ${brandCode}, url: ${url}, userId: ${userId} ${
        additionalData ? ' Additional details: ' + additionalData : ''
      }`,
      event_time: eventTime,
      env: environment,
    };
    await postLegacyEventLog(log);
  } catch {
    // Tracking must not block user flows.
  }
}

export async function logEvent(event: { step: string; [key: string]: any }) {
  try {
    const brandCode = SdkConfig.get('brandCode');
    const url = SdkConfig.get('url');
    const environment = 'Tenn';
    const eventTime = new Date().toISOString();
    const additionalData = event ? JSON.stringify(event) : '';

    logger.info(`[${eventTime}] [FE-CHATBOT-LOGS] Brand code: ${brandCode}, url: ${url}`, {
      ...event,
      event: event.step,
      url,
      ...(await getDefaultMetadata()),
    });

    // Still collection logs in old format just in case
    const log = {
      level: 'info',
      log: `[${eventTime}] [FE-CHATBOT-LOGS] Brand code: ${brandCode}, url: ${url}, ${
        additionalData ? ' Additional details: ' + additionalData : ''
      }`,
      event_time: eventTime,
      env: environment,
    };
    await postLegacyEventLog(log);
  } catch {
    // Tracking must not block user flows.
  }
}

/**
 * Report Firebase RTDB connection timing without blocking chat startup.
 */
export async function logFirebaseConnection(event: FirebaseConnectionTelemetry): Promise<void> {
  try {
    const message = `[FE-FIREBASE-CONNECTION] ${event.connection_context} ${event.outcome}`;
    const metadata = {
      ...event,
      ...(await getDefaultMetadata()),
    };

    if (event.outcome === FirebaseConnectionOutcome.Failed) {
      logger.error(message, metadata);
    } else if (event.outcome === FirebaseConnectionOutcome.Timeout) {
      logger.warn(message, metadata);
    } else {
      logger.info(message, metadata);
    }
  } catch {
    // Tracking must not block user flows.
  }
}

/**
 * Report the host `INIT` payload so client-side misconfiguration (wrong brand code, missing
 * language, unexpected login method) is diagnosable from dev logs without asking the client.
 * Tokens and PII are masked by `buildInitEventTelemetry` before anything leaves the widget.
 */
export async function logWidgetInitEvent(payload: InitializeOptions): Promise<void> {
  try {
    const telemetry = buildInitEventTelemetry(payload);
    const brandCode = telemetry.brand_code || 'unknown';
    const url = telemetry.pleased_url || 'unknown';

    logger.info(`[FE-CHATBOT-INIT] Brand code: ${brandCode}, url: ${url}`, {
      ...(await getDefaultMetadata()),
      ...telemetry,
    });
  } catch {
    // Tracking must not block user flows.
  }
}

export function getTenantFromUrl(url: string): string {
  try {
    const { hostname } = new URL(url);

    return hostname.split('.')[0] || hostname;
  } catch {
    return 'unknown';
  }
}

export function getUserIdFromStore(): string {
  const userStore = (
    store.state as {
      user: { pleasedUser: { userId?: string } };
    }
  ).user;

  return userStore.pleasedUser.userId || '';
}

export async function identifyRoute(): Promise<string> {
  const routerModule = await import('@/router');
  const router = routerModule.default;

  const matched = router.currentRoute.matched;
  const matchedPath = matched.length ? matched[matched.length - 1]?.path : undefined;
  return matchedPath || router.currentRoute.name || 'UnknownRoute';
}

export async function getDefaultMetadata() {
  const brandCode = SdkConfig.get('brandCode');
  const userId = getUserIdFromStore();
  const route = await identifyRoute();

  return {
    brand_code: brandCode,
    user_id: userId,
    route,
  };
}
